import React, { useEffect, useState } from 'react';
import api from "../api";
import { Button, Modal } from 'react-bootstrap';

/**
 * Helper to format "YYYY-MM-DD" into "MM-DD-YYYY".
 */
function formatDate(isoDateString) {
    if (!isoDateString) return '';

    const parts = isoDateString.split('-');
    if (parts.length !== 3) return isoDateString;

    const [year, month, day] = parts;
    return `${month}-${day}-${year}`;
}

function groupBookingsByBookingGroup(bookings) {
    return bookings.reduce((groups, booking) => {
        const groupKey = booking.bookingGroup || `booking-${booking.id}`;

        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }

        groups[groupKey].push(booking);
        return groups;
    }, {});
}

const UpcomingRentals = () => {
    const [upcomingBookings, setUpcomingBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [isCanceling, setIsCanceling] = useState(false);

    const fetchBookings = async () => {
        try {
            setLoading(true);

            const response = await api.get('/bookings/my-upcoming', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            setUpcomingBookings(response.data);
            setError('');
        } catch (err) {
            setError('Failed to load your upcoming bookings.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const handleCancelClick = (bookingId) => {
        setSelectedBookingId(bookingId);
        setShowCancelModal(true);
    };

    const confirmCancel = async () => {
        if (isCanceling) return;

        try {
            setIsCanceling(true);

            const response = await api.put(
                `/bookings/cancel/${selectedBookingId}`,
                {
                    reason: "Customer canceled online."
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                }
            );

            alert(response.data);

            // Backend cancels the full booking group/workorder,
            // so refetch instead of removing only one booking card.
            await fetchBookings();

        } catch (err) {
            if (err.response && err.response.data) {
                alert(err.response.data);
            } else {
                alert("Failed to cancel booking");
            }
        } finally {
            setIsCanceling(false);
            setShowCancelModal(false);
            setSelectedBookingId(null);
        }
    };

    if (loading) return <p>Loading upcoming rentals...</p>;
    if (error) return <p>{error}</p>;
    if (upcomingBookings.length === 0) {
        return <p>No upcoming rentals found.</p>;
    }

    const groupedBookings = groupBookingsByBookingGroup(upcomingBookings);

    return (
        <>
            <div>
                <h2>Upcoming Rentals</h2>

                {Object.entries(groupedBookings).map(([bookingGroup, groupBookings]) => {
                    const firstBooking = groupBookings[0];

                    const rentalItems = groupBookings.filter(booking => !booking.insurance);
                    const insuranceItems = groupBookings.filter(booking => booking.insurance);

                    const earliestStart = rentalItems.length > 0
                        ? rentalItems
                            .map(booking => booking.startDate)
                            .sort()[0]
                        : firstBooking.startDate;

                    const latestEnd = rentalItems.length > 0
                        ? rentalItems
                            .map(booking => booking.endDate)
                            .sort()
                            .reverse()[0]
                        : firstBooking.endDate;

                    const totalPrice = groupBookings.reduce(
                        (sum, booking) => sum + Number(booking.totalPrice || 0),
                        0
                    );

                    return (
                        <div
                            key={bookingGroup}
                            className="booking-card"
                            style={{
                                border: '1px solid #ccc',
                                padding: '10px',
                                marginBottom: '10px'
                            }}
                        >
                            <h4>Reservation</h4>

                            <p>
                                <strong>Rental Dates:</strong>{' '}
                                {formatDate(earliestStart)} to {formatDate(latestEnd)}
                            </p>

                            <p><strong>Items:</strong></p>
                            <ul>
                                {rentalItems.map((booking) => (
                                    <li key={booking.id}>
                                        {booking.item?.description || 'N/A'}
                                    </li>
                                ))}

                                {insuranceItems.map((booking) => (
                                    <li key={booking.id}>
                                        {booking.item?.description || 'Rental Protection Coverage'}{' '}
                                        <strong>(Nonrefundable)</strong>
                                    </li>
                                ))}
                            </ul>

                            <p>
                                <strong>Total:</strong> ${totalPrice.toFixed(2)}
                            </p>

                            <Button
                                variant="danger"
                                onClick={() => handleCancelClick(firstBooking.id)}
                                disabled={isCanceling}
                            >
                                Cancel Reservation
                            </Button>
                        </div>
                    );
                })}
            </div>

            <Modal show={showCancelModal} onHide={() => !isCanceling && setShowCancelModal(false)}>
                <Modal.Header closeButton={!isCanceling}>
                    <Modal.Title>Cancel Reservation Confirmation</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        This will cancel the entire reservation and remove all rental items in this workorder.
                    </p>
                    <p>
                        Rental Protection Coverage, if selected, is nonrefundable.
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCancelModal(false)}
                        disabled={isCanceling}
                    >
                        No, keep reservation
                    </Button>

                    <Button
                        variant="danger"
                        onClick={confirmCancel}
                        disabled={isCanceling}
                    >
                        {isCanceling ? 'Canceling...' : 'Yes, cancel reservation'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UpcomingRentals;